// Descriptive statistics and tests for the list-statistics pages (roadmap wave 2027-01).
// Pure functions; the page component only formats what these return.

export function mean(xs: number[]): number {
  return xs.reduce((s, x) => s + x, 0) / xs.length;
}

export function sortAsc(xs: number[]): number[] {
  return [...xs].sort((a, b) => a - b);
}

export function medianOfSorted(s: number[]): number {
  const n = s.length;
  return n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2;
}

export function modes(xs: number[]): { values: number[]; freq: number } {
  const counts = new Map<number, number>();
  for (const x of xs) counts.set(x, (counts.get(x) ?? 0) + 1);
  let freq = 0;
  for (const c of counts.values()) freq = Math.max(freq, c);
  if (freq < 2) return { values: [], freq: 1 };
  return {
    values: [...counts.entries()]
      .filter(([, c]) => c === freq)
      .map(([v]) => v)
      .sort((a, b) => a - b),
    freq,
  };
}

/** Sum of squared deviations from the mean. */
export function sumSquares(xs: number[]): number {
  const m = mean(xs);
  return xs.reduce((s, x) => s + (x - m) ** 2, 0);
}

export function variance(xs: number[], sample: boolean): number {
  const n = xs.length;
  if (sample && n < 2) return NaN;
  return sumSquares(xs) / (sample ? n - 1 : n);
}

/** Linear interpolation between closest ranks: Excel PERCENTILE.INC, NumPy default. */
export function percentileInc(s: number[], p: number): number {
  const h = (s.length - 1) * (p / 100);
  const lo = Math.floor(h);
  const hi = Math.ceil(h);
  return s[lo] + (h - lo) * (s[hi] - s[lo]);
}

/** Nearest-rank percentile: the smallest value with at least p% of data at or below it. */
export function percentileNearestRank(s: number[], p: number): number {
  const rank = Math.max(1, Math.ceil((p / 100) * s.length));
  return s[Math.min(rank, s.length) - 1];
}

/** Percent of values strictly below x plus half of those equal to it. */
export function percentileRank(s: number[], x: number): number {
  const below = s.filter((v) => v < x).length;
  const equal = s.filter((v) => v === x).length;
  return ((below + 0.5 * equal) / s.length) * 100;
}

/**
 * Quartiles by the median-of-halves method used by TI-83/84 calculators and most textbooks:
 * with an odd count the median itself belongs to neither half.
 */
export function quartilesTextbook(s: number[]): { q1: number; q2: number; q3: number } {
  const n = s.length;
  const q2 = medianOfSorted(s);
  if (n < 2) return { q1: s[0], q2, q3: s[0] };
  const half = Math.floor(n / 2);
  const lower = s.slice(0, half);
  const upper = s.slice(n % 2 ? half + 1 : half);
  return { q1: medianOfSorted(lower), q2, q3: medianOfSorted(upper) };
}

export function geometricMean(xs: number[]): number {
  if (xs.some((x) => x <= 0)) return NaN;
  return Math.exp(xs.reduce((s, x) => s + Math.log(x), 0) / xs.length);
}

export function harmonicMean(xs: number[]): number {
  if (xs.some((x) => x <= 0)) return NaN;
  return xs.length / xs.reduce((s, x) => s + 1 / x, 0);
}

// ---------- Distributions ----------

/** Standard normal CDF via erf (Abramowitz-Stegun 7.1.26, error < 1.5e-7). */
export function normalCdf(z: number): number {
  const t = 1 / (1 + (0.3275911 * Math.abs(z)) / Math.SQRT2);
  const y =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) *
      t *
      Math.exp(-(z * z) / 2);
  return z >= 0 ? (1 + y) / 2 : (1 - y) / 2;
}

function logGamma(x: number): number {
  // Lanczos approximation (g = 7, n = 9).
  const c = [
    0.99999999999980993, 676.5203681218851, -1259.1392167224028, 771.32342877765313,
    -176.61502916214059, 12.507343278686905, -0.13857109526572012, 9.9843695780195716e-6,
    1.5056327351493116e-7,
  ];
  if (x < 0.5) return Math.log(Math.PI / Math.sin(Math.PI * x)) - logGamma(1 - x);
  x -= 1;
  let a = c[0];
  const t = x + 7.5;
  for (let i = 1; i < 9; i += 1) a += c[i] / (x + i);
  return 0.5 * Math.log(2 * Math.PI) + (x + 0.5) * Math.log(t) - t + Math.log(a);
}

/** Regularized upper incomplete gamma Q(a, x) (Numerical Recipes gser/gcf). */
function gammaQ(a: number, x: number): number {
  if (x <= 0) return 1;
  const gln = logGamma(a);
  if (x < a + 1) {
    let ap = a;
    let sum = 1 / a;
    let del = sum;
    for (let n = 0; n < 500; n += 1) {
      ap += 1;
      del *= x / ap;
      sum += del;
      if (Math.abs(del) < Math.abs(sum) * 1e-14) break;
    }
    return 1 - sum * Math.exp(-x + a * Math.log(x) - gln);
  }
  let b = x + 1 - a;
  let c = 1 / 1e-300;
  let d = 1 / b;
  let h = d;
  for (let i = 1; i < 500; i += 1) {
    const an = -i * (i - a);
    b += 2;
    d = an * d + b;
    if (Math.abs(d) < 1e-300) d = 1e-300;
    c = b + an / c;
    if (Math.abs(c) < 1e-300) c = 1e-300;
    d = 1 / d;
    const del = d * c;
    h *= del;
    if (Math.abs(del - 1) < 1e-14) break;
  }
  return Math.exp(-x + a * Math.log(x) - gln) * h;
}

/** P(χ² ≥ x) with k degrees of freedom. */
export function chiSquarePValue(x: number, k: number): number {
  return gammaQ(k / 2, x / 2);
}

// ---------- Chi-square ----------

export type ChiSquareResult = {
  statistic: number;
  df: number;
  p: number;
  expected: number[][];
  lowExpected: number;
};

/** Goodness of fit. Expected may be counts or proportions; empty means equal. */
export function chiSquareGoodness(
  observed: number[],
  expectedRaw: number[],
): ChiSquareResult | string {
  const k = observed.length;
  if (k < 2) return 'Enter at least two observed counts.';
  if (observed.some((o) => o < 0)) return 'Counts cannot be negative.';
  const total = observed.reduce((s, o) => s + o, 0);
  if (total <= 0) return 'The observed counts add up to zero.';
  let expected: number[];
  if (!expectedRaw.length) expected = observed.map(() => total / k);
  else {
    if (expectedRaw.length !== k) return `Enter ${k} expected values, one per category.`;
    if (expectedRaw.some((e) => e <= 0)) return 'Expected values must be above zero.';
    const eSum = expectedRaw.reduce((s, e) => s + e, 0);
    // Scale to the observed total, so proportions, percentages and counts all work.
    expected = expectedRaw.map((e) => (e / eSum) * total);
  }
  const statistic = observed.reduce((s, o, i) => s + (o - expected[i]) ** 2 / expected[i], 0);
  const df = k - 1;
  return {
    statistic,
    df,
    p: chiSquarePValue(statistic, df),
    expected: [expected],
    lowExpected: expected.filter((e) => e < 5).length,
  };
}

/** Test of independence on an r × c contingency table. */
export function chiSquareIndependence(table: number[][]): ChiSquareResult | string {
  const r = table.length;
  const c = table[0]?.length ?? 0;
  if (r < 2 || c < 2) return 'Enter at least two rows with at least two numbers each.';
  if (table.some((row) => row.length !== c)) return 'Every row needs the same number of columns.';
  if (table.some((row) => row.some((v) => v < 0))) return 'Counts cannot be negative.';
  const rowSums = table.map((row) => row.reduce((s, v) => s + v, 0));
  const colSums = Array.from({ length: c }, (_, j) => table.reduce((s, row) => s + row[j], 0));
  const total = rowSums.reduce((s, v) => s + v, 0);
  if (rowSums.some((v) => v === 0) || colSums.some((v) => v === 0))
    return 'A row or column adds up to zero; remove it.';
  const expected = table.map((_, i) => colSums.map((cs) => (rowSums[i] * cs) / total));
  let statistic = 0;
  for (let i = 0; i < r; i += 1)
    for (let j = 0; j < c; j += 1)
      statistic += (table[i][j] - expected[i][j]) ** 2 / expected[i][j];
  const df = (r - 1) * (c - 1);
  return {
    statistic,
    df,
    p: chiSquarePValue(statistic, df),
    expected,
    lowExpected: expected.flat().filter((e) => e < 5).length,
  };
}
